import React from 'react';

import { Button, Result, Skeleton, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { CustomerParticipationSchema, Module, ParticipationRowStatus, RetrieveCustomerSchema } from '@app/models/users';
import { isForbiddenError } from '@app/services/apiErrors';
import { formatDate, fromUtc } from '@app/services/date';
import { formatContract } from '@app/services/formatContract';
import { useApiErrorMessage } from '@app/services/hooks/useApiErrorMessage';
import { AccessDenied } from '@components/AccessDenied';

import { useCustomersQuery } from '../../hooks';
import { ParticipationStatusTag } from '../ParticipationStatusTag';

export const CustomersContainer: React.FC = () => {
  const spaceT = useTranslation(NSKeys.spaceSettings);
  const commonT = useTranslation(NSKeys.common);
  const getErrorMessage = useApiErrorMessage();
  const customers = useCustomersQuery();

  if (customers.isLoading) {
    return <Skeleton active />;
  }
  if (isForbiddenError(customers.error)) {
    return <AccessDenied withProfileLink={false} />;
  }
  if (customers.isError) {
    return (
      <Result
        status="error"
        subTitle={getErrorMessage(customers.error)}
        extra={<Button onClick={() => customers.refetch()}>{commonT.t('buttonReload')}</Button>}
      />
    );
  }

  const columns: ColumnsType<RetrieveCustomerSchema> = [
    {
      title: spaceT.t('customers.columns.name'),
      key: 'name',
      render: (_, row) => row.legal_entity.name,
    },
    {
      title: commonT.t('inn'),
      key: 'inn',
      render: (_, row) => row.legal_entity.inn,
    },
    {
      title: spaceT.t('organization.kpp'),
      key: 'kpp',
      render: (_, row) => row.legal_entity.kpp || '—',
    },
    {
      title: spaceT.t('organization.ogrn'),
      key: 'ogrn',
      render: (_, row) => row.legal_entity.ogrn || '—',
    },
    {
      title: spaceT.t('organization.registrationDate'),
      key: 'registration_date',
      render: (_, row) => formatDate(row.legal_entity.registration_date),
    },
    {
      title: spaceT.t('organization.legalAddress'),
      key: 'legal_address',
      render: (_, row) => row.legal_entity.legal_address?.as_string || '—',
    },
  ];

  const participationColumns: ColumnsType<CustomerParticipationSchema> = [
    {
      title: spaceT.t('coalition.columns.module'),
      dataIndex: 'module',
      key: 'module',
      render: (value: Module) => commonT.t(`module.${value}` as const),
    },
    {
      title: spaceT.t('coalition.columns.contract'),
      dataIndex: 'contract_number',
      key: 'contract_number',
      render: (_, row) => formatContract(row.contract_number, row.contract_date),
    },
    {
      title: spaceT.t('coalition.columns.status'),
      dataIndex: 'status',
      key: 'status',
      render: (value: ParticipationRowStatus) => <ParticipationStatusTag status={value} />,
    },
    {
      title: spaceT.t('customers.columns.activatedAt'),
      dataIndex: 'activated_at',
      key: 'activated_at',
      render: (value: string | null) => formatDate(fromUtc(value)),
    },
  ];

  return (
    <Table<RetrieveCustomerSchema>
      rowKey="scope_id"
      className="tableClassname"
      dataSource={customers.data}
      columns={columns}
      pagination={false}
      scroll={{ x: true }}
      locale={{ emptyText: spaceT.t('customers.empty') }}
      expandable={{
        defaultExpandAllRows: true,
        expandedRowRender: (row) => (
          <Table<CustomerParticipationSchema>
            rowKey="uid"
            size="small"
            tableLayout="fixed"
            dataSource={row.participations}
            columns={participationColumns}
            pagination={false}
          />
        ),
      }}
    />
  );
};
